import * as React from 'react'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import { CalendarDays, Clock, Globe } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PublicEventType } from '../../../types'
import TimezoneSelect from './TimezoneSelect'
import { isPhoneLocation, LocationIcon } from '../locations'
import { locationLabel } from './format'

interface IProps {
  eventType: PublicEventType
  duration: number
  durations: number[]
  onDurationChange: (duration: number) => void
  timezone: string
  onTimezoneChange: (timezone: string) => void
  compact?: boolean
  // Selected date/time, shown as the first detail row once a slot is picked
  when?: React.ReactNode
}

export default function EventInfo({ eventType, duration, durations, onDurationChange, timezone, onTimezoneChange, compact, when }: IProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = React.useState(false)
  const description = React.useMemo(() => DOMPurify.sanitize(eventType.description || ''), [eventType.description])
  const hasDescription = description.replace(/<[^>]*>/g, '').trim().length > 0

  return (
    <div className='flex flex-col'>
      <Avatar className='size-6'>
        {eventType.host.avatar_url && <AvatarImage src={eventType.host.avatar_url} alt={eventType.host.name} />}
        <AvatarFallback className='text-xs'>{eventType.host.name?.[0]}</AvatarFallback>
      </Avatar>
      <p className='mt-2 text-sm font-semibold text-muted-foreground'>{eventType.host.name}</p>
      <h1 className='mt-2 text-xl font-semibold text-foreground'>{eventType.title}</h1>

      {hasDescription && (
        <div className={cn('mt-2 max-w-none text-sm text-foreground/80 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5', compact && !expanded && 'line-clamp-2')}>
          <div dangerouslySetInnerHTML={{ __html: description }} />
        </div>
      )}
      {hasDescription && compact && (
        <Button variant='link' size='sm' className='h-auto self-start p-0' onClick={() => setExpanded(e => !e)}>
          {expanded ? t('Scheduling::Less') : t('Scheduling::More')}
        </Button>
      )}

      <ul className='mt-4 flex flex-col gap-4 text-sm font-medium text-foreground/80'>
        {when && (
          <li className='flex items-start gap-2'>
            <CalendarDays className='mt-0.5 size-4 shrink-0' />
            <div>{when}</div>
          </li>
        )}
        <li className='flex items-start gap-2'>
          <Clock className='mt-0.5 size-4 shrink-0' />
          {durations.length > 1 && !when ? (
            <div className='flex flex-wrap gap-1'>
              {durations.map(d => (
                <Button key={d} size='xs' variant='outline' aria-pressed={d === duration}
                  className='h-6 rounded-md px-2 text-sm font-medium aria-pressed:border-transparent aria-pressed:bg-foreground/10'
                  onClick={() => onDurationChange(d)}>
                  {t('Scheduling::{{count}}m', { count: d })}
                </Button>
              ))}
            </div>
          ) : (
            <span>{t('Scheduling::{{count}}m', { count: duration })}</span>
          )}
        </li>
        {eventType.locations.map(location => (
          <li key={location.type} className='flex items-start gap-2'>
            <LocationIcon type={location.type} className='mt-0.5 size-4 shrink-0' />
            <div className='flex flex-col'>
              <span>{locationLabel(t, location.type)}</span>
              {location.value && (
                isPhoneLocation(location.type)
                  ? <a className='font-normal text-muted-foreground hover:underline' href={`tel:${location.value}`}>{location.value}</a>
                  : <span className='font-normal text-muted-foreground'>{location.value}</span>
              )}
            </div>
          </li>
        ))}
        <li className='flex items-start gap-2'>
          <Globe className='mt-0.5 size-4 shrink-0' />
          <TimezoneSelect value={timezone} onChange={onTimezoneChange} readOnly={!!when} />
        </li>
      </ul>
    </div>
  )
}
